import {OfferNode} from '../types'
import {useExpansion} from './ExpansionContext'
import {Children, ChildWrap, Connector, Node} from './OfferTreeNode.styled'
import {OfferTreeCard} from './OfferTreeCard'

type OfferTreeNodeProps = {
  node: OfferNode
}

/**
 * Recursively renders a single offer card and, when expanded, its nested sub-offers.
 *
 * Expansion is held in the shared {@link useExpansion} state rather than locally, so an
 * "Expand all / Collapse all" control can flip the whole tree and so deep nodes can start
 * collapsed by default. Children are only rendered while the node is expanded, so a collapsed
 * branch stays out of the DOM entirely — a large network mounts lazily as it is opened (PT-5721).
 *
 * @param node - The offer tree node to render (offer data, depth and child nodes).
 */
export const OfferTreeNode = ({node}: OfferTreeNodeProps) => {
  const {isExpanded, toggle} = useExpansion()
  const expanded = isExpanded(node)
  const hasChildren = node.children.length > 0

  return (
    <Node>
      <OfferTreeCard
        node={node}
        hasChildren={hasChildren}
        expanded={expanded}
        onToggleExpand={() => toggle(node)}
      />
      {hasChildren && expanded && (
        <Children>
          {node.children.map((child, index) => (
            <ChildWrap key={child.offer.id ?? index}>
              <Connector aria-hidden="true" />
              <OfferTreeNode node={child} />
            </ChildWrap>
          ))}
        </Children>
      )}
    </Node>
  )
}
